import {WordWithSound} from "../../../common/types/words";
import {Box, IconButton, Skeleton, Typography, useMediaQuery} from "@mui/material";
import React, {useMemo} from "react";
import BorderedSection from "./BorderedSection";
import {AnimatedContent} from "./AnimatedContent";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import {useTheme} from "@mui/material/styles";
import {useAudio} from "../../../common/context/AudioContext";
import {useDeck} from "../../deck-management/context/DeckContext";
import {useConjugation} from "../../inflections/hooks/useConjugation";
import {ConjugationView} from "../../inflections/components/ConjugationView";
import {getAudioForSource} from "../../../common/utils/audioUtils";

export interface DisplayOptions {
    order?: number
    title: string
    icon: React.ElementType
    titleRearView?: React.ReactNode
    bgcolor: string
    color: string
    addMarginAbove?: boolean
}

export interface HeaderDisplayOptions extends DisplayOptions {
    showImage: boolean
    showPos: boolean
}

export interface WordSummaryViewProps {
    word: WordWithSound | null
    displayOptions: {
        lemma?: DisplayOptions,
        lemmaHeader?: HeaderDisplayOptions,
        translation?: DisplayOptions,
        translationHeader?: HeaderDisplayOptions,
        exampleSentence?: DisplayOptions
        conjugation?: DisplayOptions
    }
    animationDirection: 1 | -1
}

function WordSummaryView({word, displayOptions, animationDirection: direction}: WordSummaryViewProps) {

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const { play } = useAudio();

    const viewOrder : React.ReactNode[] = useMemo(() => {
        return [
            {options: displayOptions.lemma, view: lemmaView},
            {options: displayOptions.lemmaHeader, view: headerForLemmaView},
            {options: displayOptions.translation, view: translationView},
            {options: displayOptions.translationHeader, view: headerForTranslationView},
            {options: displayOptions.exampleSentence, view: exampleSentenceView},
            {options: displayOptions.conjugation, view: conjugationView}
        ].filter(option => !!option.options)
         .sort((a, b) => (a.options!.order ?? 0) - (b.options!.order ?? 0))
         .map((option) => option.view());
    }, [displayOptions]);

    return (
        <div>
            {viewOrder.map((view, index) => (
                <div key={index}>
                    {view}
                </div>
            ))}
        </div>
    )

    function wordHeaderView(headerOptions: HeaderDisplayOptions, children: React.ReactElement) {
        return (
            <BorderView options={headerOptions} direction={direction} animationKey={word?.wordId!} noPadding={true}>
                <Box sx={{display: "flex", alignItems: "center", justifyContent: "left"}}>
                    {!word && (
                        <Skeleton variant="rectangular"
                                  sx={{width: {xs: 100, sm: 120}, height: {xs: 100, sm: 120}, mr: 2, borderRadius: 2}}
                        />
                    )}
                    {word && (
                        <Box component="img"
                         src={headerOptions.showImage && !!word.resources?.image ? word.resources.image : "logo_small.png"}
                         sx={{height: {xs: 100, sm: 120}, width: {xs: 100, sm: 120}, objectFit: "cover", flexShrink: 0, borderRadius: 2, mr: 2}}
                        />
                    )}
                    <Box>
                        <Typography variant={isMobile ? "h5" : "h4"} align="left">
                            {word && children}
                        </Typography>
                        <Typography variant={"body1"} align="left">
                            {!word ? <Skeleton/> : headerOptions.showPos ? <PosWithTaggingText realWord={word}/> : ""}
                        </Typography>
                    </Box>
                </Box>
            </BorderView>
        )
    }

    function bareLemmaView() {
        return (
            <>
                {!word && <Skeleton/>}
                {word && word!.lemma}
                {word && word!.sound && (
                    <IconButton onClick={() => play(word.sound!)} sx={{color: "black"}} title="Play pronunciation">
                        <VolumeUpIcon/>
                    </IconButton>
                )}
            </>
        )
    }

    function headerForLemmaView() {
        return wordHeaderView(displayOptions.lemmaHeader!, bareLemmaView())
    }

    function bareTranslationView() {
        return (<>
                {!word ? <Skeleton/> : word.translations.join(", ")}
            </>)
    }

    function headerForTranslationView() {
        return wordHeaderView(displayOptions.translationHeader!, bareTranslationView())
    }

    function lemmaView() {
        return (
            <BorderView options={displayOptions.lemma!} direction={direction} animationKey={word?.wordId!}>
                <Typography variant={isMobile ? "h5" : "h4"} align="center">
                    {bareLemmaView()}
                </Typography>
            </BorderView>
        )
    }

    function translationView() {
        return (
            <BorderView options={displayOptions.translation!} direction={direction} animationKey={word?.wordId!}>
                <Typography variant={isMobile ? "h5" : "h4"} align="center">
                    {bareTranslationView()}
                </Typography>
            </BorderView>
        )
    }

    function exampleSentenceView() {
        const sentenceAudio = getAudioForSource(word?.resources?.sentence);
        return (
            <BorderView options={displayOptions.exampleSentence!} direction={direction} animationKey={word?.wordId!}>
                <Typography variant={"body1"} align="left" sx={ word ? {display: "flex", alignItems: "center", justifyContent: "left"} : {}}>
                    {!word && <Skeleton/>}
                    {word && (word.exampleSentences?.[0]?.sentence ?? "No example sentence available.")}
                    {sentenceAudio && (
                        <IconButton onClick={() => play(sentenceAudio)} sx={{color: "black"}} title="Play sentence" size={"small"}>
                            <VolumeUpIcon/>
                        </IconButton>
                    )}
                </Typography>
                {word?.exampleSentences?.[0]?.translation && (
                    <Typography variant={"body1"} align="left" sx={{mt: 1, fontStyle: "italic"}}>
                        {word.exampleSentences?.[0]?.translation}
                    </Typography>
                )}
            </BorderView>
        )
    }

    function conjugationView() {
        return word && (
            <SafeConjugationView realWord={word}
                                 wordId={word.wordId!}
                                 options={displayOptions.conjugation!}
                                 direction={direction}/>
        );
    }
}

function PosWithTaggingText({realWord}: {realWord: WordWithSound}) {

    const { currentDeck } = useDeck();
    const { posWithTagging } = useConjugation({word: realWord, language: currentDeck?.language ?? ""});

    return (
        <>
            {posWithTagging}
        </>
    )
}

function SafeConjugationView({realWord, wordId, options, direction} : {realWord: WordWithSound, wordId: string, options: DisplayOptions, direction: 1 | -1}) {

    const { currentDeck } = useDeck();
    const { hasConjugationInfo } = useConjugation({word: realWord, language: currentDeck?.language ?? ""});

    if (!hasConjugationInfo) {
        return null;
    }

    return (
        <BorderView options={options} direction={direction} animationKey={wordId} showBox={false} noPadding={true}>
            <Typography variant={"body1"} align="left">
                <ConjugationView word={realWord} language={currentDeck?.language ?? ""} />
            </Typography>
        </BorderView>
    )
}

interface BorderViewProps {
    options: DisplayOptions;
    direction: 1 | -1;
    animationKey: string;
    showBox?: boolean;
    noPadding?: boolean;
    children: React.ReactNode;
}

function BorderView({options, direction, animationKey, showBox = true, noPadding = false, children} : BorderViewProps) {

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    return (
        <BorderedSection icon={options.icon}
                         title={options.title}
                         titleRearView={options.titleRearView}
                         marginTop={options.addMarginAbove ? (isMobile ? "12px" : "20px") : 0}
                         showBox={showBox}
                         sx={{
                             backgroundColor: options.bgcolor,
                             color: options.color,
                             minHeight: isMobile ? 35 : 60,
                             p: noPadding ? 0 : 1.5,
                         }}
        >
            <AnimatedContent animationKey={animationKey} direction={direction}>
                {children}
            </AnimatedContent>
        </BorderedSection>
    )
}

export default WordSummaryView;